import { useState, useEffect, useCallback } from 'react';
import { Heart, Zap, Lightbulb, MessageCircle, Pencil, Send, ChevronDown, ChevronUp } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { analytics } from '../utils/analytics';
import ReplyCard from './ReplyCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SECS_MIN = 60;
const SECS_HOUR = 3600;
const SECS_DAY = 86400;

function timeAgo(d) {
  const s = Math.floor((new Date() - new Date(d)) / 1000);
  if (s < SECS_MIN) return 'just now';
  if (s < SECS_HOUR) return `${Math.floor(s / SECS_MIN)}m ago`;
  if (s < SECS_DAY) return `${Math.floor(s / SECS_HOUR)}h ago`;
  return `${Math.floor(s / SECS_DAY)}d ago`;
}

export const VentCard = ({ vent, sessionId, onUpdate }) => {
  const [reacted, setReacted] = useState({ has_hearted: false, has_lightninged: false });
  const [hearts, setHearts] = useState(vent.hearts);
  const [lightnings, setLightnings] = useState(vent.lightnings);
  const [busy, setBusy] = useState(false);
  const [showReplyInput, setShowReplyInput] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [replying, setReplying] = useState(false);
  const [replies, setReplies] = useState([]);
  const [showReplies, setShowReplies] = useState(false);
  const [repliesCount, setRepliesCount] = useState(vent.replies_count || 0);
  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState(vent.content);
  const [content, setContent] = useState(vent.content);

  const isOwner = vent.session_id === sessionId;

  const fetchReactions = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/vents/${vent.id}/user-reactions?session_id=${sessionId}`);
      setReacted(r.data);
    } catch (err) {
      if (process.env.NODE_ENV === 'development') console.warn('[Vent:reactions]', err?.message);
    }
  }, [vent.id, sessionId]);

  useEffect(() => { fetchReactions(); }, [fetchReactions]);

  const handleReaction = async (type) => {
    if (busy) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/vents/react`, { vent_id: vent.id, reaction_type: type, session_id: sessionId });
      const added = r.data.action === 'added';
      analytics.reaction(type, added ? 'added' : 'removed');
      if (type === 'heart') { setHearts(p => added ? p+1 : p-1); setReacted(p => ({...p, has_hearted: added})); }
      else { setLightnings(p => added ? p+1 : p-1); setReacted(p => ({...p, has_lightninged: added})); }
    } catch { toast.error('Failed to react'); }
    finally { setBusy(false); }
  };

  const handleReply = async () => {
    if (!replyText.trim() || replying) return;
    setReplying(true);
    try {
      const r = await axios.post(`${API}/vents`, { topic_id: vent.topic_id, content: replyText.trim(), session_id: sessionId, parent_id: vent.id });
      setReplies(p => [...p, r.data]);
      setRepliesCount(p => p + 1);
      setReplyText('');
      setShowReplyInput(false);
      setShowReplies(true);
      toast.success('Reply posted!');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to reply'); }
    finally { setReplying(false); }
  };

  const toggleReplies = async () => {
    if (!showReplies && replies.length === 0 && repliesCount > 0) {
      try {
        const r = await axios.get(`${API}/vents/${vent.id}/replies`);
        setReplies(r.data);
      } catch { toast.error('Failed to load replies'); }
    }
    setShowReplies(p => !p);
  };

  const saveEdit = async () => {
    try {
      const r = await axios.put(`${API}/vents/${vent.id}`, { content: editText.trim(), session_id: sessionId });
      setContent(r.data.content);
      setEditing(false);
      toast.success('Vent updated!');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to update'); }
  };

  const isSuggestion = vent.is_suggestion;

  return (
    <div className={`rounded-2xl p-4 sm:p-5 ${isSuggestion ? 'glass border border-vent-secondary/20' : 'glass'}`} data-testid={`vent-card-${vent.id}`}>
      {isSuggestion && (
        <div className="flex items-center gap-1.5 mb-3" data-testid={`suggestion-badge-${vent.id}`}>
          <Lightbulb className="w-3.5 h-3.5 text-vent-secondary" />
          <span className="text-vent-secondary text-xs font-heading font-bold tracking-widest uppercase">Topic Suggestion</span>
        </div>
      )}

      {editing ? (
        <div className="mb-3">
          <textarea value={editText} onChange={e => setEditText(e.target.value)} className="w-full bg-black/40 border border-vent-primary/30 rounded-xl p-3 text-white text-sm resize-none focus:outline-none" rows={3} data-testid={`edit-textarea-${vent.id}`} />
          <div className="flex gap-2 mt-2">
            <button onClick={saveEdit} className="px-3 py-1.5 bg-vent-primary text-white text-xs font-bold rounded-lg" data-testid={`edit-save-${vent.id}`}>Save</button>
            <button onClick={() => { setEditing(false); setEditText(content); }} className="px-3 py-1.5 bg-zinc-800 text-zinc-400 text-xs rounded-lg" data-testid={`edit-cancel-${vent.id}`}>Cancel</button>
          </div>
        </div>
      ) : (
        <p className={`text-base leading-relaxed mb-3 ${isSuggestion ? 'text-white font-medium' : 'text-zinc-300'}`}>{content}</p>
      )}

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className="text-zinc-600 text-xs">{timeAgo(vent.created_at)}</span>
          {isOwner && !editing && (
            <button onClick={() => setEditing(true)} className="text-zinc-600 hover:text-zinc-400 transition-colors" data-testid={`edit-btn-${vent.id}`}>
              <Pencil className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
        <div className="flex items-center gap-3">
          <button onClick={() => setShowReplyInput(p => !p)} className={`flex items-center gap-1 text-xs transition-colors ${showReplyInput ? 'text-vent-primary' : 'text-zinc-500 hover:text-zinc-300'}`} data-testid={`reply-btn-${vent.id}`}>
            <MessageCircle className="w-3.5 h-3.5" />
            {repliesCount > 0 && <span>{repliesCount}</span>}
          </button>
          <button onClick={() => handleReaction('heart')} disabled={busy} className={`flex items-center gap-1 transition-colors ${reacted.has_hearted ? 'text-red-500' : 'text-zinc-500 hover:text-red-400'}`} data-testid={`heart-btn-${vent.id}`}>
            <Heart className="w-3.5 h-3.5" fill={reacted.has_hearted ? 'currentColor' : 'none'} />
            <span className="text-xs">{hearts}</span>
          </button>
          <button onClick={() => handleReaction('lightning')} disabled={busy} className={`flex items-center gap-1 transition-colors ${reacted.has_lightninged ? 'text-yellow-500' : 'text-zinc-500 hover:text-yellow-400'}`} data-testid={`lightning-btn-${vent.id}`}>
            <Zap className="w-3.5 h-3.5" fill={reacted.has_lightninged ? 'currentColor' : 'none'} />
            <span className="text-xs">{lightnings}</span>
          </button>
        </div>
      </div>

      {showReplyInput && (
        <div className="mt-3 flex gap-2">
          <input value={replyText} onChange={e => setReplyText(e.target.value)} placeholder="Reply anonymously..." className="flex-1 bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-vent-primary/50" onKeyDown={e => { if (e.key === 'Enter') handleReply(); }} data-testid={`reply-input-${vent.id}`} />
          <button onClick={handleReply} disabled={!replyText.trim() || replying} className="p-2 bg-vent-primary/20 text-vent-primary rounded-xl disabled:opacity-30" data-testid={`reply-send-${vent.id}`}>
            <Send className="w-4 h-4" />
          </button>
        </div>
      )}

      {repliesCount > 0 && (
        <button onClick={toggleReplies} className="mt-3 flex items-center gap-1 text-xs text-zinc-500 hover:text-zinc-300 transition-colors" data-testid={`toggle-replies-${vent.id}`}>
          {showReplies ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
          {showReplies ? 'Hide' : `Show ${repliesCount}`} {repliesCount === 1 ? 'reply' : 'replies'}
        </button>
      )}

      {showReplies && replies.length > 0 && (
        <div className="mt-3 space-y-2">
          {replies.map(r => <ReplyCard key={r.id} vent={r} sessionId={sessionId} />)}
        </div>
      )}
    </div>
  );
};

export default VentCard;
